import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyPortfolio } from '../services/apiManagerDashboard';
import { useAuthStore } from '../store/useAuthStore';

const STAGE_LABELS = {
    new: 'Nuevo',
    contacted: 'Contactado',
    qualified: 'Calificado',
    proposal: 'Propuesta',
    negotiation: 'Negociación',
    won: 'Ganado',
    lost: 'Perdido',
};

const STAGE_COLORS = {
    new: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    contacted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    qualified: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    proposal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    negotiation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const PRIORITY_COLORS = {
    alta: 'text-red-500 font-bold',
    media: 'text-amber-500 font-semibold',
    normal: 'text-text-secondary',
};

const PRIORITY_LABELS = {
    alta: 'ALTA',
    media: 'Media',
    normal: 'Normal',
};

function DaysBadge({ days }) {
    if (days === 0) return <span className="text-emerald-500 font-semibold text-sm">Hoy</span>;
    if (days >= 5) return (
        <span className="inline-flex items-center gap-1 text-red-500 font-bold text-sm">
            <span className="material-symbols-outlined text-[16px]">warning</span>
            {days} días
        </span>
    );
    if (days >= 3) return (
        <span className="inline-flex items-center gap-1 text-orange-500 font-semibold text-sm">
            <span className="material-symbols-outlined text-[16px]">schedule</span>
            {days} días
        </span>
    );
    return <span className="text-slate-500 text-sm">{days} día{days !== 1 ? 's' : ''}</span>;
}

export default function MyPortfolio() {
    const user = useAuthStore(s => s.user);
    const [filter, setFilter] = useState('all'); // 'all' | 'neglected' | 'urgent'

    const { data: contactsRaw, isLoading } = useQuery({
        queryKey: ['my-portfolio'],
        queryFn: getMyPortfolio,
        refetchInterval: 30000,
    });
    const contacts = Array.isArray(contactsRaw) ? contactsRaw : [];

    const neglectedCount = contacts.filter(c => c.days_since_contact >= 3).length;
    const urgentCount = contacts.filter(c => c.priority === 'alta').length;

    const filtered = contacts.filter(c => {
        if (filter === 'neglected') return c.days_since_contact >= 3;
        if (filter === 'urgent') return c.priority === 'alta';
        return true;
    });

    return (
        <div className="flex-1 overflow-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-background-dark border-b border-gray-100 dark:border-gray-800 px-8 py-5">
                <div className="flex items-end justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Mi Cartera</h1>
                        <p className="text-sm text-text-secondary mt-0.5">
                            Hola, <span className="text-primary font-semibold">{user?.name}</span>. Aquí están tus contactos asignados.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {neglectedCount > 0 && (
                            <div className="flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 px-3 py-1.5 rounded-lg">
                                <span className="material-symbols-outlined text-red-500 text-[16px]">warning</span>
                                <span className="text-red-600 dark:text-red-400 text-sm font-bold">{neglectedCount} descuidados</span>
                            </div>
                        )}
                        <p className="text-sm text-text-secondary">{contacts.length} contactos activos</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 mt-4">
                    {[
                        { key: 'all', label: 'Todos' },
                        { key: 'neglected', label: `Sin contacto (≥3d) · ${neglectedCount}` },
                        { key: 'urgent', label: `Urgentes · ${urgentCount}` },
                    ].map(f => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${filter === f.key
                                ? 'bg-primary text-white shadow-md shadow-primary/20'
                                : 'bg-gray-100 dark:bg-gray-800 text-text-secondary hover:text-text-main'
                                }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="px-8 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <span className="material-symbols-outlined text-emerald-500 text-[32px]">check_circle</span>
                        </div>
                        <p className="text-lg font-semibold">¡Todo al día!</p>
                        <p className="text-sm text-text-secondary">No tienes contactos descuidados.</p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                        {/* Table header */}
                        <div className="grid grid-cols-[2fr_1.2fr_1.1fr_1.5fr_0.8fr] gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/60 text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-gray-100 dark:border-gray-800">
                            <span>Cliente</span>
                            <span>Estado</span>
                            <span>Días sin contacto</span>
                            <span>Próxima tarea</span>
                            <span>Prioridad</span>
                        </div>

                        {/* Rows */}
                        {filtered.map((c, idx) => (
                            <div
                                key={c.id}
                                className={`grid grid-cols-[2fr_1.2fr_1.1fr_1.5fr_0.8fr] gap-4 px-6 py-4 items-center border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors cursor-pointer ${c.days_since_contact >= 3 ? 'border-l-4 border-l-red-400' : ''}`}
                            >
                                <div>
                                    <p className="font-semibold text-sm">{c.full_name}</p>
                                    {c.company && <p className="text-xs text-text-secondary">{c.company}</p>}
                                </div>
                                <div>
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STAGE_COLORS[c.pipeline_stage] ?? ''}`}>
                                        {STAGE_LABELS[c.pipeline_stage] ?? c.pipeline_stage}
                                    </span>
                                </div>
                                <div>
                                    <DaysBadge days={c.days_since_contact} />
                                </div>
                                <div>
                                    {c.next_activity ? (
                                        <span className="text-sm text-text-main">{c.next_activity}</span>
                                    ) : (
                                        <span className="text-xs text-text-secondary italic">Sin tarea pendiente</span>
                                    )}
                                </div>
                                <div>
                                    <span className={`text-sm ${PRIORITY_COLORS[c.priority] ?? ''}`}>
                                        {PRIORITY_LABELS[c.priority] ?? c.priority}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Quote */}
                <p className="text-xs text-text-secondary italic mt-6 text-center">
                    "El CRM debe actuar como un asistente personal, recordando al vendedor a quién ha descuidado."
                </p>
            </div>
        </div>
    );
}
